import { useState, useMemo } from 'react'
import { useDishLibrary } from '../lib/hooks'
import LoadingScreen from '../components/LoadingScreen'

const SORTS = [
  { key: 'protein', label: 'Protein', defaultDir: -1 },
  { key: 'kcal', label: 'Calories', defaultDir: -1 },
  { key: 'carbs', label: 'Carbs', defaultDir: -1 },
  { key: 'fat', label: 'Fat', defaultDir: -1 },
  { key: 'name', label: 'A-Z', defaultDir: 1 },
]
const HIGH_PROTEIN_THRESHOLD = 15

// Dishes without a verified real photo get a category emoji on a tinted
// background (derived from the name) instead of a wrong or missing picture.
const CATEGORIES = [
  [/chutney|pickle|achar|inji|podi|thokku|gojju|dip|sauce|paste|masala powder|gravy base/i, '🌶️', '#cf4a3a'],
  [/soup|shorba/i, '🍲', '#e8a33d'],
  [/laddu|ladoo|barfi|halwa|kheer|payasam|jalebi|rasgulla|gulab|peda|sandesh|kulfi|phirni|rabri|basundi|sweet|mithai|pudding|shrikhand|malpua|gujiya|khurma|meetha|ice cream|cake|brownie|cookie|chikki/i, '🍮', '#d9548a'],
  [/lassi|smoothie|shake|juice|chai|tea|coffee|thandai|buttermilk|chaas|milk|water|drink|sharbat/i, '🥤', '#4aa3d9'],
  [/salad|raita|sprouts|chaat|bhel|kachumber/i, '🥗', '#5cb85c'],
  [/dal|dhal|sambar|rasam|kadhi|rajma|chole|chana|lobia|usal|misal/i, '🥣', '#d98c2b'],
  [/roti|paratha|naan|kulcha|puri|poori|bhatura|thepla|phulka|chapati|bhakri|dosa|uttapam|cheela|chilla|idli|appam|crepe/i, '🫓', '#c9a15a'],
  [/rice|biryani|pulao|khichdi|poha|upma|bisi|bath|fried rice|pongal|millet|oats|porridge|daliya/i, '🍚', '#e0c36a'],
  [/pizza|burger|sandwich|pasta|noodle|maggi|roll|wrap|toast|macaroni|chowmein|hakka/i, '🥪', '#e07a4a'],
  [/pakora|pakoda|vada|samosa|bajji|bhaji|tikki|kebab|cutlet|fritter|chips|namkeen|bhujia|mathri|chakli|murukku|snack|fry|65|manchurian/i, '🥟', '#d9733d'],
  [/paneer|tofu|cheese/i, '🧀', '#e8b84a'],
]
function categoryFor(name) {
  for (const [re, emoji, tint] of CATEGORIES) if (re.test(name)) return { emoji, tint }
  return { emoji: '🍛', tint: '#c97b3a' }
}

function DishThumb({ dish, size }) {
  if (dish.image) {
    return <img src={dish.image} alt="" loading="lazy" className={`dish-card-thumb dish-card-thumb-${size}`} />
  }
  const { emoji, tint } = categoryFor(dish.name)
  return (
    <div
      className={`dish-card-thumb dish-card-thumb-${size} dish-card-thumb-placeholder`}
      style={{ background: `linear-gradient(135deg, ${tint}55, ${tint}18)`, borderColor: `${tint}66` }}
      aria-hidden="true"
    >
      {emoji}
    </div>
  )
}

function DishCard({ dish, expanded, onToggle }) {
  const recipe = dish.recipe
  return (
    <div className={`dish-card ${expanded ? 'is-expanded' : ''}`}>
      <button type="button" className="dish-card-head" onClick={onToggle} aria-expanded={expanded}>
        <DishThumb dish={dish} size="sm" />
        <div className="dish-card-info">
          <div className="dish-card-name-row">
            <span className="dish-card-name">{dish.name}</span>
            {dish.protein >= HIGH_PROTEIN_THRESHOLD && <span className="dish-card-badge">HIGH PROTEIN</span>}
          </div>
          <div className="dish-card-macros">
            <span>{dish.kcal} kcal</span>
            <span>{dish.carbs}g carbs</span>
            <span>{dish.fat}g fat</span>
          </div>
        </div>
        <div className="dish-card-protein-col">
          <span className="dish-card-protein">{dish.protein}</span>
          <span className="dish-card-protein-unit">g protein</span>
        </div>
        <span className="dish-card-chevron">{expanded ? '▴' : '▾'}</span>
      </button>
      {expanded && recipe && (
        <div className="recipe-detail">
          {dish.image && <img src={dish.image} alt={dish.name} loading="lazy" className="recipe-detail-image" />}
          <p className="recipe-section-label">INGREDIENTS</p>
          <ul className="recipe-list">
            {recipe.ingredients.map((ing, i) => (
              <li key={i}>{ing}</li>
            ))}
          </ul>
          <p className="recipe-section-label">STEPS</p>
          <ol className="recipe-list recipe-steps">
            {recipe.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
          <p className="recipe-tip">
            <strong>Health tip:</strong> {recipe.healthTip}
          </p>
          <p className="recipe-tip">
            <strong>Lighter swap:</strong> {recipe.healthierSwap}
          </p>
        </div>
      )}
    </div>
  )
}

// All prepared dishes from the shared library/dishes catalogue, each with
// a full recipe (ingredients, steps, a health tip, and a lighter swap) -
// browsable independent of any personal plan, so this page stays reachable
// even for accounts without an AI plan yet (see Health.jsx's aiPlan gate).
export default function Dishes() {
  const { dishes, loading } = useDishLibrary()
  const [query, setQuery] = useState('')
  const [expandedKey, setExpandedKey] = useState(null)
  const [sortKey, setSortKey] = useState('protein')
  const [sortDir, setSortDir] = useState(-1)

  const dishList = useMemo(() => {
    if (!dishes) return []
    return Object.entries(dishes)
      .filter(([, d]) => d.recipe)
      .map(([key, d]) => ({ key, ...d }))
  }, [dishes])

  const avgProtein = useMemo(
    () => (dishList.length ? Math.round(dishList.reduce((s, d) => s + d.protein, 0) / dishList.length) : 0),
    [dishList],
  )
  const highProteinCount = useMemo(() => dishList.filter((d) => d.protein >= HIGH_PROTEIN_THRESHOLD).length, [dishList])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const list = q ? dishList.filter((d) => d.name.toLowerCase().includes(q)) : dishList
    const sorted = [...list].sort((a, b) => {
      if (sortKey === 'name') return a.name.localeCompare(b.name) * sortDir
      return (a[sortKey] - b[sortKey]) * sortDir
    })
    return sorted
  }, [dishList, query, sortKey, sortDir])

  const pickSort = (key, defaultDir) => {
    if (key === sortKey) {
      setSortDir((d) => -d)
    } else {
      setSortKey(key)
      setSortDir(defaultDir)
    }
  }

  if (loading) return <LoadingScreen message="Loading recipes" />

  return (
    <div className="section">
      <span className="plan-eyebrow">● RECIPE LIBRARY</span>
      <h1 className="display-heading" style={{ fontSize: 'clamp(30px, 8vw, 80px)', margin: '16px 0 32px' }}>
        Dishes
      </h1>

      <div className="stats-row" style={{ marginBottom: 24 }}>
        <div className="stat-block is-hero">
          <span className="stat-block-label">RECIPES</span>
          <span className="stat-block-value">{dishList.length}</span>
        </div>
        <div className="stat-block">
          <span className="stat-block-label">HIGH PROTEIN (15G+)</span>
          <span className="stat-block-value">{highProteinCount}</span>
        </div>
        <div className="stat-block">
          <span className="stat-block-label">AVG PROTEIN / DISH</span>
          <span className="stat-block-value">{avgProtein}<small>g</small></span>
        </div>
      </div>

      <div className="food-select" style={{ marginBottom: 16 }}>
        <input
          type="text"
          className="food-select-input"
          placeholder="Search dishes..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      <div className="dish-sort-row">
        <span className="dish-sort-label">SORT BY</span>
        {SORTS.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`btn dish-sort-btn ${sortKey === s.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => pickSort(s.key, s.defaultDir)}
          >
            {s.label}
            {sortKey === s.key && <span className="dish-sort-arrow">{sortDir === -1 ? ' ▾' : ' ▴'}</span>}
          </button>
        ))}
      </div>

      <p className="card-subtitle" style={{ margin: '14px 0' }}>
        Showing {filtered.length} of {dishList.length} - tap a dish for ingredients, steps, and a health tip.
      </p>

      {filtered.length === 0 ? (
        <p className="card-subtitle">No dishes match "{query}".</p>
      ) : (
        <div className="dish-grid">
          {filtered.map((dish) => (
            <DishCard
              key={dish.key}
              dish={dish}
              expanded={expandedKey === dish.key}
              onToggle={() => setExpandedKey(expandedKey === dish.key ? null : dish.key)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
